import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ValueChangeEvent } from '@interfaces/form.interface';
import {
  CommentIdeaService,
  CommentService,
  MemberService,
} from '@arkius/shared-api';
import { PropositionMenuOption } from '../proposition-menu/proposition-menu.component';
import {
  AlertController,
  ModalController,
  NavController,
} from '@ionic/angular';
import { take } from 'rxjs';
import { ModerateCommentModalComponent } from '../../modals/moderate-comment-modal/moderate-comment-modal.component';
import { MentionService } from '@app/services/mention.service';
import { environment } from '@shared-envs';

@Component({
  selector: 'ark-lib-comment',
  templateUrl: './comment.component.html',
  styleUrl: './comment.component.css',
})
export class CommentComponent implements OnInit {
  @Input() comment!: any;
  @Input() showReplies = false;
  @Input() showReplyInput = false;
  @Input() entityType!: 'problem' | 'idea' | 'solution' | 'thinkTank';

  @Output() commented = new EventEmitter<any>();
  @Output() replied = new EventEmitter<any>();
  @Output() replyClicked = new EventEmitter<any>();
  @Output() upvoted = new EventEmitter<any>();
  @Output() downvoted = new EventEmitter<any>();
  @Output() upvotedReply = new EventEmitter<any>();
  @Output() downvotedReply = new EventEmitter<any>();
  @Output() commentDeleted = new EventEmitter<void>();
  @Output() replyDeleted = new EventEmitter<void>();
  @Output() commentReplied = new EventEmitter<void>();

  isEditingComment: boolean = false;
  isEditingReply: boolean = false;
  updatedComment!: string;
  updatedReply!: string;

  menuOptions: PropositionMenuOption[] = [];
  mentionListNicknames: string[] = [];
  mentionListUserIds: number[] = [];
  mentionMap = new Map<string, number>();

  constructor(
    private router: Router,
    private navController: NavController,
    private commentService: CommentService,
    private alertController: AlertController,
    private modalCtrl: ModalController,
    private mentionService: MentionService,
    private memberService: MemberService,
    private commentIdeaService: CommentIdeaService,
  ) {}

  ngOnInit() {
    environment.log(
      'CommentComponent initialized with comment:',
      this.comment.commentId,
    );
    if (this.comment.isMine && !this.comment.isModerated) {
      this.menuOptions = [
        {
          name: 'Edit Comment',
          icon: 'edit',
          handler: () => this.toggleEditModeComment(),
        },
        {
          name: 'Delete Comment',
          icon: 'trash',
          handler: () => this.deleteComment(),
        },
      ];
    } else if (!this.comment.isMine && this.entityType !== 'idea') {
      this.menuOptions.push(
        {
          name: 'Report illegal content',
          icon: 'report-flag',
          handler: () =>
            this.navController.navigateForward(
              `/report/${this.comment.commentId}/comment/illegal-content`,
            ),
        },
        {
          separatorName: 'Moderate',
        },
        // {
        //   name: 'Submit as inaccurate',
        //   icon: 'report-inaccurate',
        //   handler: () =>
        //     this.navController.navigateForward(
        //       `/report/${this.comment.commentId}/comment/inaccurate`
        //     ),
        // },
        {
          name: 'Submit as inappropriate',
          icon: 'report-inappropriate',
          handler: () =>
            this.navController.navigateForward(
              `/report/${this.comment.commentId}/comment/inappropriate`,
            ),
        },
      );
    }
  }

  toggleReply(): void {
    if (this.replyClicked.observed) {
      this.replyClicked.emit();
      return;
    }
    this.showReplies = !this.showReplies;
    if (!this.showReplies) {
      this.showReplyInput = false;
    }
  }

  toggleShowReply(): void {
    this.showReplyInput = !this.showReplyInput;
    if (this.showReplyInput) {
      this.showReplies = true;
    }
  }

  onReply(event: ValueChangeEvent): void {
    const formattedReply = this.mentionService.formatMentions(
      event.value,
      this.mentionMap,
    );
    this.replied.emit({ value: formattedReply, comment: this.comment });
    this.showReplyInput = false;
  }

  onNicknameClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.router.navigate([
      `/account/profile/${this.comment.createdByUserId}/solutions`,
    ]);
  }

  toggleEditModeComment(): void {
    this.isEditingComment = !this.isEditingComment;
    if (this.isEditingComment) {
      this.updatedComment = this.mentionService.stripMentions(
        this.comment.description,
      );
    }
  }

  updateComment(): void {
    const formattedComment = this.mentionService.formatMentions(
      this.updatedComment,
      this.mentionMap,
    );
    const body = { Description: formattedComment };

    if (this.entityType !== 'idea') {
      this.commentService
        .apiV5CommentCommentIdPut(Number(this.comment.commentId), body)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.comment.description = formattedComment;
            this.isEditingComment = false;
          },
          error: (err) => console.error('Failed to update comment:', err),
        });
    } else {
      this.commentIdeaService
        .apiV5CommentIdeaCommentIdeaIdPut(Number(this.comment.commentId), body)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.comment.description = formattedComment;
            this.isEditingComment = false;
          },
          error: (err) => console.error('Failed to update comment idea:', err),
        });
    }
  }

  onUpdatedCommentChange(event: any): void {
    this.updatedComment = event;
  }

  onActionIconClicked(event: any): void {
    this.updatedComment = event.value;
    this.updateComment();
  }

  updateReply(replyId: string): void {
    const formattedReply = this.mentionService.formatMentions(
      this.updatedReply,
      this.mentionMap,
    );
    const body = { Description: formattedReply };

    const updateLocalReply = () => {
      const reply = this.comment?.replies?.find(
        (r: any) =>
          r.commentReplyId === replyId || r.commentIdeaReplyId === replyId,
      );

      if (reply) {
        reply.description = formattedReply;
        reply.isEditing = false;
      }
    };

    if (this.entityType !== 'idea') {
      this.commentService
        .apiV5CommentCommentReplyIdPut(Number(replyId), body)
        .pipe(take(1))
        .subscribe({
          next: updateLocalReply,
          error: (error) => console.error('Failed to update reply:', error),
        });
    } else {
      this.commentIdeaService
        .apiV5CommentIdeaCommentIdeaReplyIdPut(Number(replyId), body)
        .pipe(take(1))
        .subscribe({
          next: updateLocalReply,
          error: (error) =>
            console.error('Failed to update idea reply:', error),
        });
    }
  }

  onEditReply(event: { replyId: string; description: string }): void {
    const reply =
      this.entityType === 'idea'
        ? this.comment.replies.find(
            (r: any) => r.commentReplyIdeaId === event.replyId,
          )
        : this.comment.replies.find(
            (r: any) => r.commentReplyId === event.replyId,
          );

    if (reply) {
      reply.isEditing = true;
      reply.updatedReply = event.description;
    }
  }

  deleteComment() {
    this.alertController
      .create({
        header: 'Confirm Delete',
        message:
          'Are you sure you want to delete this comment? This cannot be undone.',
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              environment.log('Delete comment cancelled.');
            },
          },
          {
            text: 'Delete',
            handler: () => {
              if (this.entityType !== 'idea') {
                this.commentService
                  .apiV5CommentCommentIdDelete(this.comment.commentId)
                  .pipe(take(1))
                  .subscribe({
                    next: (response) => {
                      environment.log(
                        'comment deleted successfully:',
                        response,
                      );
                      this.commentDeleted.emit();
                    },
                    error: (error) => {
                      console.error('Failed to delete comment:', error);
                    },
                  });
              } else {
                this.commentIdeaService
                  .apiV5CommentIdeaCommentIdeaIdDelete(this.comment.commentId)
                  .pipe(take(1))
                  .subscribe({
                    next: (response) => {
                      environment.log(
                        'comment deleted successfully:',
                        response,
                      );
                      this.commentDeleted.emit();
                    },
                    error: (error) => {
                      console.error('Failed to delete comment:', error);
                    },
                  });
              }
            },
          },
        ],
      })
      .then((alert) => {
        alert.present();
      });
  }

  OnReplyDeleted(): void {
    this.replyDeleted.emit();
  }

  async openModerateCommentModal() {
    const modal = await this.modalCtrl.create({
      component: ModerateCommentModalComponent,
      componentProps: {
        comment: this.comment,
      },
    });

    const modalElement = modal.shadowRoot?.querySelector(
      '.modal-wrapper',
    ) as HTMLElement;
    if (modalElement) {
      modalElement.style.width = '90%';
      modalElement.style.borderRadius = '8px';
    }

    // Present the modal and wait for it to be dismissed
    await modal.present();
    await modal.onDidDismiss();
  }

  getPlainText(description: string): string {
    return this.mentionService.stripMentions(description);
  }

  onMentionSearch(searchTerm: string) {
    environment.log('Search term received:', searchTerm);

    if (searchTerm.length >= 3) {
      this.memberService.apiV5MemberMemberNicknamesGet(searchTerm).subscribe({
        next: (users) => {
          users.forEach((user: any) => {
            // Stores the nickname and userId in a map
            if (!this.mentionMap.has(user.nickname)) {
              this.mentionMap.set(user.nickname, user.userId);
            }
          });

          this.mentionListNicknames = Array.from(this.mentionMap.keys());
          this.mentionListUserIds = Array.from(this.mentionMap.values());

          environment.log('Updated Mention Map:', this.mentionMap);
        },
        error: (err) => {
          console.error('Error fetching mentions:', err);
        },
      });
    }
  }
}
